import DynamicImage from '@/components/commons/dynamic-image';
import {
  BlogPostElementDataType,
  BlogPostListElementDataType,
} from '@/components/sections/builder/elements/body/blog-post/type';
import { getOverrideAnimationParams } from '@/components/sections/builder/settings/animation-setting';
import InputSelect from '@/components/sections/builder/settings/input-select';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { ElementProps } from '@/components/sections/builder/types';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { dummyBlogPosts } from '@/constant/dummyData/dummy-blogpost';
import { useBlogsList } from '@/hooks/api/storefront/blogs';

import { cn, removeHTMLTags, truncateText } from '@/lib/utils';
import { useBuilderStore } from '@/stores/builder';
import { format } from 'date-fns';
import { Columns3 } from 'lucide-react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { shallow } from 'zustand/shallow';

const defaultData: BlogPostListElementDataType = {
  name: 'Blog Post',
  source: '1',
  columns: 4,
  columnsMobile: 2,
  size: 'small',
  showImage: true,
  showDate: true,
  showAuthor: true,
  showCategory: true,
  showShadow: true,
  showContent: true,
  rounded: 10,
  gap: 1,
  sorting: 'desc',
};

export const ElementContent = ({ element }: ElementProps) => {
  const { category } = useParams() as { category: string };

  const content = (element.data || defaultData) as BlogPostListElementDataType;
  const parentContent = element.parentData as BlogPostElementDataType;

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const { data: listBlogs } = useBlogsList(
    category,
    {
      sortBy: 'created_at',
      sortDirection: content.sorting || defaultData.sorting,
    },
    {
      enabled: !!category,
    }
  );

  const blogs = !listBlogs?.length ? dummyBlogPosts : listBlogs || [];

  return (
    <>
      <div
        className={cn(
          'grid place-content-center',
          `grid-cols-${content.columnsMobile} md:grid-cols-${content.columns} gap-3 md:gap-8 [&>article>div>div>div]:text-sm [&>article>div>div>h2]:text-lg [&>article>div>div>p]:text-sm [&>article>div>div>span]:text-sm`,
          'p-3',
          'lg:pb-[--p-bottom] lg:pl-[--p-left] lg:pr-[--p-right] lg:pt-[--p-top]'
        )}
        style={
          {
            gap: `${content.gap || 4}px`,
            '--p-top': `${content.padding?.top || 0}px`,
            '--p-bottom': `${content.padding?.bottom || 0}px`,
            '--p-left': `${content.padding?.left || 0}px`,
            '--p-right': `${content.padding?.right || 0}px`,
          } as React.CSSProperties
        }
      >
        {blogs?.map((blog, index: number) => (
          <article
            key={index}
            data-animation='true'
            data-animation-group={`blog-item-${index}`}
            {...getOverrideAnimationParams(content.animation, index)}
          >
            <Link href={`/blogs/${blog.slug_category}/${blog.slug}`}>
              <div
                className={cn(
                  'theme_animation_card_hover theme_blog_card flex flex-col justify-between overflow-hidden rounded-none',
                  content.showShadow && 'shadow-md'
                )}
              >
                <div className=''>
                  {content.showImage && (
                    <div className='image_padding'>
                      <DynamicImage
                        width={500}
                        height={500}
                        className='aspect-[3/2] w-full object-cover'
                        src={blog.image_banner}
                        alt={blog.title || 'Blog image'}
                      />
                    </div>
                  )}
                  <div className='flex flex-col items-start justify-start pb-1 pt-4'>
                    <span
                      className={cn('mb-3 rounded px-1 py-0.5 text-left text-xs', 'badge_scheme')}
                    >
                      {blog.name}
                    </span>
                    <h2 className='mb-2 line-clamp-2 text-left text-lg md:line-clamp-3'>
                      {truncateText(blog.title, builder.theme?.blogCard?.limitCharacters as number)}
                    </h2>
                    {content.showContent && (
                      <p className='mb-5 line-clamp-3 font-light'>
                        {removeHTMLTags(blog.content || 'No description available')}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <div className='flex items-center justify-between text-gray-500'>
                    {content.showDate && (
                      <span>{format(new Date(blog.created_at), 'MMMM dd, yyyy')}</span>
                    )}
                    {content.showAuthor && (
                      <div className='flex items-center space-x-2'>
                        <span className='font-medium'>{blog.author}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          </article>
        ))}
      </div>
      <style jsx>
        {`
          @media (min-width: 768px) {
            .blog-post-list-padding {
              padding-top: ${content.padding?.top || 0}px;
              padding-bottom: ${content.padding?.bottom || 0}px;
              padding-left: ${content.padding?.left || 0}px;
              padding-right: ${content.padding?.right || 0}px;
            }
          }

          .badge_scheme {
            background-color: ${parentContent?.colorSchemeBlogPostList?.btnSolidBackgroud};
            color: ${parentContent?.colorSchemeBlogPostList?.btnSolidlabel};
          }

          .card-rounded {
            border-radius: ${content.rounded || 0}px;
          }

          .post-gap {
            margin-right: ${content.gap || 0}px;
          }
        `}
      </style>
    </>
  );
};

export const ElementPreviewIcon = Columns3;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as BlogPostListElementDataType;

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Section Blog Post</h1>
      <div className='mb-3 grid grid-cols-2 items-center gap-2'>
        <h4>Sorting</h4>
        <InputSelect
          defaultValue={data.sorting ?? defaultData.sorting}
          options={[
            { label: 'Newest first', value: 'desc' },
            { label: 'Oldest first', value: 'asc' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, { sorting: v })
          }
        />
      </div>

      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4>Number of columns on desktop</h4>
        <InputSlider
          value={data.columns ?? 1}
          max={5}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              columns: val,
            })
          }
        />
      </div>
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4>Number of columns on mobile</h4>
        <InputSlider
          value={data.columnsMobile ?? 3}
          max={2}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              columnsMobile: val,
            })
          }
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label htmlFor='showImage'>Show image</label>
        <Switch
          id='showImage'
          aria-label='Select show image'
          className='ml-auto'
          checked={data.showImage ?? defaultData.showImage}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showImage: !!checked,
            });
          }}
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label htmlFor='showDate'>Show date</label>
        <Switch
          id='showDate'
          aria-label='Select show date'
          className='ml-auto'
          checked={data.showDate ?? defaultData.showDate}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showDate: !!checked,
            });
          }}
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label htmlFor='showContent'>Show Content</label>
        <Switch
          id='showContent'
          aria-label='Select show content'
          className='ml-auto'
          checked={data.showContent ?? defaultData.showContent}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showContent: !!checked,
            });
          }}
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <div className='mb-4 grid grid-cols-2 items-center gap-3'>
        <h4>Gap Post</h4>
        <InputSlider
          value={data.gap ?? 0}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              gap: val,
            })
          }
        />
      </div>
      <PaddingSetting
        value={data.padding}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            padding: v,
          })
        }
      />
    </div>
  );
}

const BlogPostListEmail = {
  type: 'blog_post_input_email',
  group: 'blog_post',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default BlogPostListEmail;
